// Copyright (c) Docugami, Inc. All rights reserved.

import { useEffect, useRef } from "react";
import { Selection, Breadcrumb, DetailsList, DetailsListLayoutMode, IBreadcrumbItem, IColumn, Icon, IDetailsHeaderProps, Link, ScrollablePane, SelectionMode, Sticky, StickyPositionType, IDetailsRowProps, TooltipHost, TooltipOverflowMode } from "@fluentui/react";
import styled from "styled-components";
import { FontSizes, FontWeights } from '@fluentui/react/lib/Styling';
import { FileIconType, getFileTypeIconProps } from '@fluentui/react-file-type-icons';
import { graphPickerClassNames } from "./GraphStyles";
import { appContentColor } from "components/shell/presentation/AppStyles";
import { ItemTypeFilter, SelectionMode as StorageProviderSelectionMode } from "./StorageProviderFilePicker";

const DisabledRow = styled.div`
    opacity: .3;
`

const Root = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
`

const ListContainer = styled.div`
    flex: 1;
    position: relative;
`

const ItemText = styled.span`
    font-size: ${FontSizes.smallPlus};
    color: ${appContentColor};
    font-weight: ${FontWeights.regular};
`

const ItemNameText = styled(ItemText)`
    color: black;
    font-size: ${FontSizes.medium};
`

export interface GraphFolder {
    id: string;
    parent: string;
    path: string;
    webUrl: string;
    name: string;
    children: GraphItem[];
}

export interface GraphItem {
    id: string;
    name: string;
    webUrl: string;
    size: string;
    lastModified: string;
    lastModifiedBy: string;
    isFolder: boolean;
    extension?: string;
}

interface GraphItemPickerProps {
    folder?: GraphFolder;
    breadCrumbs: IBreadcrumbItem[];
    loadFolder: (id?: string) => Promise<void>;
    onSelectionChanged: (items: GraphItem[]) => void;
    extensionFilter?: string[];
    itemTypeFilter: ItemTypeFilter;
    selectionMode: StorageProviderSelectionMode;
}

export const GraphItemPicker = (props: GraphItemPickerProps): JSX.Element => {
    const selectionRef = useRef<Selection<GraphItem>>(new Selection({
        onSelectionChanged: () => onSelectionChanged(),
        getKey: item => item.id,
        canSelectItem: canSelect
    }));

    useEffect(() => {
        if (!props.folder) {
            navToFolder();
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.folder]);

    async function navToFolder(id?: string) {
        await props.loadFolder(id);
    }

    function onSelectionChanged() {
        props.onSelectionChanged(selectionRef.current.getSelection());
    }

    const columns: IColumn[] = [
        {
            key: 'column1',
            name: 'File Type',
            iconName: 'Page',
            className: graphPickerClassNames.fileIconCell,
            iconClassName: graphPickerClassNames.fileIconHeaderIcon,
            isIconOnly: true,
            minWidth: 16,
            maxWidth: 16,
            onRender: (item: GraphItem) => getIcon(item)
        },
        {
            key: 'column2',
            name: 'Name',
            minWidth: 150,
            isRowHeader: true,
            isResizable: true,
            onRender: (item: GraphItem) => {
                return (
                    <TooltipHost content={item.name} overflowMode={TooltipOverflowMode.Parent}>
                        {
                            item.isFolder ?
                            <Link data-selection-disabled={true} style={{ color: "black" }} onClick={() => navToFolder(item.id)}><ItemNameText>{item.name}</ItemNameText></Link>
                            :
                            <ItemNameText>{item.name}</ItemNameText>
                        }
                    </TooltipHost>
                );
            }
        },
        {
            key: 'column3',
            name: 'Last Modified',
            isResizable: true,
            minWidth: 125,
            onRender: (item: GraphItem) => <ItemText>{item.lastModified}</ItemText>
        },
        {
            key: 'column4',
            name: 'Modified By',
            isResizable: true,
            minWidth: 100,
            onRender: (item: GraphItem) => <ItemText>{item.lastModifiedBy}</ItemText>
        },
        {
            key: 'column5',
            name: 'Size',
            isResizable: true,
            minWidth: 70,
            onRender: (item: GraphItem) => <ItemText>{item.size}</ItemText>
        },
        {
            key: 'column6',
            name: 'ExternalLink',
            isIconOnly: true,
            minWidth: 16,
            maxWidth: 16,
            onRender: (item: GraphItem) => <a target='_blank' href={item.webUrl} rel={'noreferrer'} style={{ color: 'inherit' }}><Icon iconName="OpenInNewTab" /></a>
        }
    ];

    async function onItemInvoked(item: GraphItem) {
        if (item.isFolder) {
            navToFolder(item.id);
        }
    }

    function renderHeader(props?: IDetailsHeaderProps, defaultRender?: (props?: IDetailsHeaderProps) => JSX.Element | null): JSX.Element | null {
        if (!props || !defaultRender) {
            return null;
        }

        return (
            <Sticky stickyPosition={StickyPositionType.Header}>
                {defaultRender(props)}
            </Sticky>
        );
    }

    function canSelect(item: GraphItem) {
        if (props.itemTypeFilter === ItemTypeFilter.File && item.isFolder) {
            return false;
        }
        else if (props.itemTypeFilter === ItemTypeFilter.Folder && !item.isFolder) {
            return false;
        }

        return !props.extensionFilter || item.isFolder || props.extensionFilter.includes(item.extension?.toLowerCase() ?? '')
    }

    function renderRow(props?: IDetailsRowProps, defaultRender?: (props?: IDetailsRowProps) => JSX.Element | null): JSX.Element | null {
        if (!props || !defaultRender) {
            return null;
        }

        if (!canSelect(props.item)) {
            return (
                <DisabledRow>
                    {defaultRender(props)}
                </DisabledRow>
            );
        }

        return defaultRender(props);
    }

    return (
        <Root>
            <Breadcrumb items={props.breadCrumbs} />
            <ListContainer>
                <ScrollablePane>
                    <DetailsList
                        items={props.folder?.children ?? []}
                        columns={columns}
                        layoutMode={DetailsListLayoutMode.justified}
                        selectionMode={props.selectionMode === StorageProviderSelectionMode.Single ? SelectionMode.single : SelectionMode.multiple}
                        onItemInvoked={onItemInvoked}
                        isHeaderVisible={true}
                        selectionPreservedOnEmptyClick={true}
                        onRenderDetailsHeader={renderHeader}
                        setKey='set'
                        onRenderRow={renderRow}
                        // @ts-ignore
                        selection={selectionRef.current}
                    />
                </ScrollablePane>
            </ListContainer>
        </Root>
    );
}

const getIcon = (item: GraphItem) => {
    if (item.isFolder) {
        return <Icon className={graphPickerClassNames.fileIconImg} {...getFileTypeIconProps({ type: FileIconType.folder })} />
    }
    else if (!item.extension) {
        return <Icon className={graphPickerClassNames.fileIconImg} {...getFileTypeIconProps({ type: FileIconType.genericFile })} />
    }
    else {
        const props = getFileTypeIconProps({ extension: item.extension });
        return !!props.iconName ?
            <Icon className={graphPickerClassNames.fileIconImg} {...props} /> :
            <Icon className={graphPickerClassNames.fileIconImg} {...props} />
    }
}
